/**
 * Timeline row: the walking glyph.
 *
 * Two different drivers either side of 750px.
 *
 * Desktop (>=750px): the band sits in normal flow at its natural height and the
 * glyph is DRAGGED along the rule. Pointer Events with pointer capture, one
 * axis, clamped to the first and last item centres. Release snaps her to the
 * nearest centre on a short ease; clicking an item (or the rule beside it)
 * sends her walking there. She is a real control: role="slider", tabbable,
 * Left/Right (and Home/End) step her item to item. Because none of that
 * announces itself, entering the band on a hover-capable pointer rocks her on
 * her feet once (CSS keyframes on the glyph inside the marker, so the wobble
 * never touches the transform the drag owns), rate-limited to one sway per
 * --lf-tl-wobble-cooldown and cancelled the moment she is actually grabbed.
 *
 * Mobile (<750px): unchanged scroll-driven walk. The glyph tracks the
 * scrollport's mid-line down the vertical rule, so the item level with the
 * middle of the screen is the one in focus. Nothing listens for wheel or touch
 * events, so the page always scrolls straight through the band.
 *
 * Scroll container: Horizon's squeeze layout makes `.page-wrapper` the page
 * scroller at >=990px and `document.scrollingElement` below it. That is above
 * the 750px axis flip, so the mobile path always reads the document, but the
 * reads still go through @theme/scroll-container rather than assuming it.
 *
 * Motion values (hop, tilt, stride rate, sway, focus window, snap) are read
 * from the custom properties declared in lf-timeline-row.css, which is where
 * they are locked. Read once per refresh, never per frame.
 *
 * Reduced motion: desktop keeps the drag and the focus handover but drops the
 * walk cycle, the hover sway and the ease, so she jumps. Mobile parks her over the first item and
 * stops, leaving every item solid. No JS at all gives the same static band.
 */

import { getScrollContainer, getScrollEventTarget, getIntersectionRoot } from '@theme/scroll-container';

const MOBILE_QUERY = '(max-width: 749px)';
const MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

class LfTimelineRow extends HTMLElement {
  connectedCallback() {
    this.inner = this.querySelector('[data-lf-tl-inner]');
    this.rail = this.querySelector('[data-lf-tl-rail]');
    this.marker = this.querySelector('[data-lf-tl-marker]');
    this.items = Array.from(this.querySelectorAll('[data-lf-tl-item]'));

    if (!this.inner || !this.rail || !this.marker || this.items.length === 0) return;

    this.mobile = window.matchMedia(MOBILE_QUERY);
    this.reduced = window.matchMedia(MOTION_QUERY);
    this.hover = window.matchMedia(HOVER_QUERY);
    this.index = 0;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.refresh = this.refresh.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMarkerBlur = this.onMarkerBlur.bind(this);
    this.onRailClick = this.onRailClick.bind(this);
    this.onBandEnter = this.onBandEnter.bind(this);
    this.onWobbleEnd = this.onWobbleEnd.bind(this);

    this.mobile.addEventListener('change', this.refresh);
    this.reduced.addEventListener('change', this.refresh);
    this.hover.addEventListener('change', this.refresh);
    window.addEventListener('resize', this.onResize, { passive: true });

    this.refresh();
  }

  disconnectedCallback() {
    this.stop();
    this.mobile?.removeEventListener('change', this.refresh);
    this.reduced?.removeEventListener('change', this.refresh);
    this.hover?.removeEventListener('change', this.refresh);
    window.removeEventListener('resize', this.onResize);
  }

  /* ---- lifecycle ------------------------------------------------------- */

  /** Re-read the mode (axis, reduced motion, knobs) and restart the driver. */
  refresh() {
    this.stop();

    this.axis = this.mobile.matches ? 'y' : 'x';
    this.wobble = this.reduced.matches ? 0 : 1;
    this.readTuning();
    this.measure();

    if (this.axis === 'x') {
      // Desktop: she is dragged, so there is nothing to observe or listen to.
      // Reduced motion keeps the control and the focus handover, not the wobble.
      this.index = clamp(this.index, 0, this.items.length - 1);
      this.setAttribute('data-lf-tl-live', '');
      this.setAttribute('data-lf-tl-draggable', '');
      this.bindDrag();
      this.bindWobble();
      this.upgradeMarker();
      this.moveTo(this.centres[this.index]);
      return;
    }

    this.draw(0);
    if (this.reduced.matches) return;

    this.setAttribute('data-lf-tl-live', '');

    // Only run the per-frame work while the band is anywhere near the scrollport.
    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible) this.listen();
        else this.mute();
      },
      { root: getIntersectionRoot(), rootMargin: '100% 0px' }
    );
    this.observer.observe(this);
  }

  stop() {
    this.mute();
    this.cancelAnimation();
    this.unbindDrag();
    this.unbindWobble();
    this.downgradeMarker();
    this.observer?.disconnect();
    this.observer = null;
    this.removeAttribute('data-lf-tl-live');
    this.removeAttribute('data-lf-tl-draggable');
    this.removeAttribute('data-lf-tl-dragging');
    this.active = null;
    this.items.forEach((item) => item.classList.remove('is-active'));
  }

  listen() {
    if (this.listening) return;
    this.listening = true;
    // Held so mute() detaches from the same target even if the breakpoint has
    // moved in between.
    this.scrollTarget = getScrollEventTarget();
    this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  mute() {
    if (!this.listening) return;
    this.listening = false;
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this.scrollTarget = null;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  /* ---- measurement ----------------------------------------------------- */

  /**
   * Motion values, read off the custom properties once per refresh. They are
   * declared and locked in lf-timeline-row.css; the fallbacks below only cover
   * a stylesheet that has not landed yet, so they mirror the same numbers.
   */
  readTuning() {
    const styles = getComputedStyle(this);
    const read = (name, fallback) => {
      const value = parseFloat(styles.getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    };

    this.tuning = {
      hop: read('--lf-tl-hop', 4),
      tilt: read('--lf-tl-tilt-max', 4),
      strideRate: Math.max(0, read('--lf-tl-stride-rate', 0.5)),
      sway: read('--lf-tl-sway', 1),
      focus: Math.max(0, read('--lf-tl-focus', 1)),
      snap: Math.max(0, read('--lf-tl-snap', 250)),
      wobbleCooldown: Math.max(0, read('--lf-tl-wobble-cooldown', 4000)),
    };
  }

  /**
   * The box the mobile walk is measured against: the page scroller's own
   * scrollport, not the window.
   */
  scrollport() {
    const container = getScrollContainer();
    const isRoot = container === document.scrollingElement || container === document.documentElement;

    return {
      top: isRoot ? 0 : container.getBoundingClientRect().top,
      height: container.clientHeight || window.innerHeight,
    };
  }

  measure() {
    const railRect = this.rail.getBoundingClientRect();

    // Item centres in rail-local coordinates: the glyph travels between the
    // first and the last, so she is dead over an item at both ends of the walk
    // exactly as the design draws it.
    this.centres = this.items.map((item) => {
      const rect = item.getBoundingClientRect();
      return this.axis === 'x'
        ? rect.left - railRect.left + rect.width / 2
        : rect.top - railRect.top + rect.height / 2;
    });

    this.first = this.centres[0];
    this.last = this.centres[this.centres.length - 1];
    this.span = this.last - this.first;
  }

  onResize() {
    // A resize mid-drag would yank her out from under the pointer.
    if (this.dragging) return;
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.refresh();
    });
  }

  /* ---- the walk -------------------------------------------------------- */

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.draw(this.progress());
    });
  }

  /** Mobile only. 0 at the start of the walk, 1 at the end. */
  progress() {
    const port = this.scrollport();
    const railTop = this.rail.getBoundingClientRect().top - port.top;
    if (this.span <= 0) return 0;

    return clamp((port.height / 2 - railTop - this.first) / this.span, 0, 1);
  }

  draw(progress) {
    this.render(this.first + this.span * progress, progress);
  }

  /** Put her at an absolute rail-local position, clamped to the travel range. */
  moveTo(position) {
    const bounds = this.span >= 0 ? [this.first, this.last] : [this.last, this.first];
    this.pos = clamp(position, bounds[0], bounds[1]);
    this.render(this.pos, this.span === 0 ? 0 : (this.pos - this.first) / this.span);
  }

  render(position, progress) {
    // Walk cycle: `stride rate` steps per gap between items, so the bob reads
    // as strides rather than a wobble at a fixed rate. Driven by how far she
    // has travelled, never by a clock, so it stops dead when she does.
    const strides = Math.max(1, this.items.length - 1) * this.tuning.strideRate;
    const phase = progress * strides * Math.PI * 2;
    const lift = -Math.abs(Math.sin(phase)) * this.tuning.hop * this.wobble;
    const sway = Math.sin(phase) * this.tuning.sway * this.wobble;
    const tilt = Math.sin(phase) * this.tuning.tilt * this.wobble;

    if (this.axis === 'x') {
      this.style.setProperty('--lf-tl-pos-x', `${position}px`);
      this.style.setProperty('--lf-tl-wob-x', '0px');
      this.style.setProperty('--lf-tl-wob-y', `${lift.toFixed(2)}px`);
    } else {
      this.style.setProperty('--lf-tl-pos-y', `${position}px`);
      this.style.setProperty('--lf-tl-wob-x', `${sway.toFixed(2)}px`);
      this.style.setProperty('--lf-tl-wob-y', '0px');
    }

    this.style.setProperty('--lf-tl-tilt', `${tilt.toFixed(2)}deg`);
    this.setActive(position);
  }

  /** Index of the item centre nearest a rail-local position. */
  nearestIndex(position) {
    let nearest = 0;
    let best = Infinity;

    this.centres.forEach((centre, index) => {
      const distance = Math.abs(centre - position);
      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });

    return nearest;
  }

  setActive(position) {
    const nearest = this.nearestIndex(position);
    const best = Math.abs(this.centres[nearest] - position);

    // Focus window: how close the glyph has to be before an item sharpens,
    // measured as a share of the spacing between item centres. At 1 she is
    // always within range of her nearest item (the furthest she can ever be is
    // half a gap), so every item hands straight over to the next.
    const gaps = Math.max(1, this.centres.length - 1);
    const spacing = Math.abs(this.span) / gaps;
    const inRange = spacing <= 0 || best <= this.tuning.focus * spacing;
    const next = inRange ? nearest : null;

    if (next === this.active) return;
    this.active = next;
    this.items.forEach((item, index) => item.classList.toggle('is-active', index === next));

    // Keyboard stepping continues from wherever she actually is.
    if (next !== null) {
      this.index = next;
      this.syncAria();
    }
  }

  /* ---- desktop drag ---------------------------------------------------- */

  bindDrag() {
    if (this.dragBound) return;
    this.dragBound = true;
    // Pointer capture retargets move/up to the marker, so one element carries
    // the whole gesture.
    this.marker.addEventListener('pointerdown', this.onPointerDown);
    this.marker.addEventListener('pointermove', this.onPointerMove);
    this.marker.addEventListener('pointerup', this.onPointerUp);
    this.marker.addEventListener('pointercancel', this.onPointerUp);
    this.marker.addEventListener('lostpointercapture', this.onPointerUp);
    this.marker.addEventListener('keydown', this.onKeyDown);
    this.marker.addEventListener('blur', this.onMarkerBlur);
    this.rail.addEventListener('click', this.onRailClick);
  }

  unbindDrag() {
    if (!this.dragBound) return;
    this.dragBound = false;
    this.dragging = false;
    this.pointerId = null;
    this.marker.removeEventListener('pointerdown', this.onPointerDown);
    this.marker.removeEventListener('pointermove', this.onPointerMove);
    this.marker.removeEventListener('pointerup', this.onPointerUp);
    this.marker.removeEventListener('pointercancel', this.onPointerUp);
    this.marker.removeEventListener('lostpointercapture', this.onPointerUp);
    this.marker.removeEventListener('keydown', this.onKeyDown);
    this.marker.removeEventListener('blur', this.onMarkerBlur);
    this.marker.removeAttribute('data-lf-tl-pointer-focus');
    this.rail.removeEventListener('click', this.onRailClick);
  }

  onPointerDown(event) {
    if (this.axis !== 'x' || event.button > 0) return;

    this.cancelAnimation();
    // She is being grabbed, so the "you can grab me" hint has done its job —
    // kill it mid-sway rather than let it fight the drag for the next beat.
    this.clearWobble();
    this.dragging = true;
    this.dragMoved = false;
    this.suppressClick = false;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartPos = this.pos;

    try {
      this.marker.setPointerCapture(event.pointerId);
    } catch (_) {
      // Capture is a nicety; the gesture still works without it.
    }

    this.setAttribute('data-lf-tl-dragging', '');
    // The focus below is script focus, which engines still score as
    // :focus-visible; the flag tells the CSS this one came from a pointer so the
    // keyboard ring stays off a mouse or touch grab.
    this.marker.setAttribute('data-lf-tl-pointer-focus', '');
    this.marker.focus({ preventScroll: true });
    // Blocks the browser's native image drag on mouse. Touch scrolling is
    // governed by touch-action in the CSS, not by this.
    event.preventDefault();
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    const delta = event.clientX - this.dragStartX;
    if (Math.abs(delta) > 2) this.dragMoved = true;
    this.moveTo(this.dragStartPos + delta);
  }

  onPointerUp(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    this.dragging = false;
    this.pointerId = null;
    this.removeAttribute('data-lf-tl-dragging');

    try {
      this.marker.releasePointerCapture(event.pointerId);
    } catch (_) {
      // Already released (lostpointercapture, or the pointer went away).
    }

    // A drag that ends over an item still fires a click on it. Swallow that one
    // so the release snap is not immediately overruled by a click-to-walk.
    if (this.dragMoved) {
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }

    this.walkTo(this.nearestIndex(this.pos), this.tuning.snap);
  }

  onKeyDown(event) {
    // Any key on her means a keyboard user is driving now, so the ring comes
    // back even if the focus started as a click.
    this.marker.removeAttribute('data-lf-tl-pointer-focus');

    if (this.axis !== 'x') return;

    let index = this.index;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        index -= 1;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        index += 1;
        break;
      case 'Home':
        index = 0;
        break;
      case 'End':
        index = this.centres.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.walkTo(clamp(index, 0, this.centres.length - 1), this.tuning.snap);
  }

  /** The flag only describes the current focus, so it dies with it. */
  onMarkerBlur() {
    this.marker.removeAttribute('data-lf-tl-pointer-focus');
  }

  /** Click an item, or the rule beside it, to send her walking there. */
  onRailClick(event) {
    if (this.axis !== 'x') return;
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    if (this.marker.contains(event.target)) return;
    if (event.target.closest?.('a, button, input, select, textarea')) return;

    const target = event.clientX - this.rail.getBoundingClientRect().left;
    const index = this.nearestIndex(target);
    const gapsCrossed = Math.max(1, Math.abs(index - this.index));

    // Longer trips get a little more time so she keeps walking rather than
    // teleporting, but the whole thing stays under a beat.
    this.walkTo(index, Math.min(700, this.tuning.snap * gapsCrossed));
  }

  /* ---- hover affordance ------------------------------------------------
   * Nobody drags a thing they do not know is draggable. Entering the BAND (not
   * the glyph — you would never aim at her first) rocks her on her feet for a
   * beat, which is the whole tell.
   *
   * The animation itself is CSS, on the glyph <img> INSIDE the marker. The
   * marker's own `transform` is the drag — anchoring, walking bob and tilt all
   * live in that one declaration — so animating the marker would overwrite the
   * drag outright. The <img> has no transform of its own, so the two never
   * write the same property. This file only opens and closes the switch.
   */

  bindWobble() {
    if (this.wobbleBound) return;
    // Touch has no meaningful mouseenter, and reduced motion wants none of it.
    if (!this.hover.matches || this.reduced.matches) return;

    this.wobbleBound = true;
    this.addEventListener('mouseenter', this.onBandEnter);
    // Bubbles up from the glyph.
    this.marker.addEventListener('animationend', this.onWobbleEnd);
  }

  unbindWobble() {
    this.clearWobble();
    this.wobbleAt = 0;
    if (!this.wobbleBound) return;
    this.wobbleBound = false;
    this.removeEventListener('mouseenter', this.onBandEnter);
    this.marker.removeEventListener('animationend', this.onWobbleEnd);
  }

  /** Once per entry, and never more than once per cooldown. */
  onBandEnter() {
    if (this.dragging) return;

    const now = performance.now();
    if (this.wobbleAt && now - this.wobbleAt < this.tuning.wobbleCooldown) return;
    this.wobbleAt = now;

    this.setAttribute('data-lf-tl-wobble', '');
    // Backstop: animationend is the real close, but a glyph that never animates
    // (no artwork, an engine that skips it) must not leave the switch on.
    if (this.wobbleTimer) clearTimeout(this.wobbleTimer);
    this.wobbleTimer = setTimeout(() => this.clearWobble(), this.tuning.wobbleCooldown);
  }

  onWobbleEnd(event) {
    if (event.animationName === 'lf-tl-wobble') this.clearWobble();
  }

  /** Removing the attribute ends the animation wherever it had got to. */
  clearWobble() {
    if (this.wobbleTimer) clearTimeout(this.wobbleTimer);
    this.wobbleTimer = null;
    this.removeAttribute('data-lf-tl-wobble');
  }

  /* ---- animation ------------------------------------------------------- */

  /** Ease her to an item centre. Reduced motion jumps instead. */
  walkTo(index, duration) {
    this.cancelAnimation();
    this.index = clamp(index, 0, this.centres.length - 1);
    this.syncAria();

    const target = this.centres[this.index];
    if (this.reduced.matches || duration <= 0 || target === this.pos) {
      this.moveTo(target);
      return;
    }

    const from = this.pos;
    const delta = target - from;
    const start = performance.now();

    const step = (now) => {
      const t = clamp((now - start) / duration, 0, 1);
      this.moveTo(from + delta * easeOutCubic(t));
      this.animFrame = t < 1 ? requestAnimationFrame(step) : null;
    };

    this.animFrame = requestAnimationFrame(step);
  }

  cancelAnimation() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    this.animFrame = null;
  }

  /* ---- the marker as a control ----------------------------------------- */

  /**
   * Desktop only. The marker ships decorative so the no-JS and mobile band stay
   * honest; it becomes a real slider only once there is something to drag.
   */
  upgradeMarker() {
    const heading = this.querySelector('.lf-timeline-row__heading')?.textContent.trim();

    this.marker.removeAttribute('aria-hidden');
    this.marker.setAttribute('role', 'slider');
    this.marker.setAttribute('tabindex', '0');
    this.marker.setAttribute('aria-orientation', 'horizontal');
    this.marker.setAttribute('aria-valuemin', '1');
    this.marker.setAttribute('aria-valuemax', String(this.items.length));
    this.marker.setAttribute('aria-label', heading ? `${heading} timeline` : 'Timeline');
    this.markerUpgraded = true;
    this.syncAria();
  }

  downgradeMarker() {
    if (!this.markerUpgraded) return;
    this.markerUpgraded = false;
    this.marker.setAttribute('aria-hidden', 'true');
    ['role', 'tabindex', 'aria-orientation', 'aria-valuemin', 'aria-valuemax', 'aria-label', 'aria-valuenow', 'aria-valuetext'].forEach(
      (name) => this.marker.removeAttribute(name)
    );
  }

  syncAria() {
    if (!this.markerUpgraded) return;
    this.marker.setAttribute('aria-valuenow', String(this.index + 1));
    const text = this.items[this.index]?.textContent.replace(/\s+/g, ' ').trim();
    if (text) this.marker.setAttribute('aria-valuetext', text);
  }
}

if (!customElements.get('lf-timeline-row')) {
  customElements.define('lf-timeline-row', LfTimelineRow);
}
